import React, { useState } from "react";
import { Context } from "@hotwired/stimulus";
import { ReactWrapperController } from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import { CopyToClipboardButton } from "@root/server/client/esweb/components/ui/copy_to_clipboard.stimulus.tsx";
import { Button } from "@root/server/client/esweb/components/ui/button.tsx";
import { Input } from "@root/server/client/esweb/components/ui/input.tsx";
import { Label } from "@root/server/client/esweb/components/ui/label.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@root/server/client/esweb/components/ui/tabs.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@root/server/client/esweb/components/ui/card.tsx";
import { cn } from "@root/server/client/esweb/lib/utils.ts";

export const name = "app--integration-view";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <IntegrationView {...props} />);
    }
}

interface IntegrationViewProps {
    webhookUrl: string;
}

function IntegrationView({ webhookUrl }: IntegrationViewProps) {
    
    const examples = {
        curl: {
            label: "cURL",
            code: `curl -X POST "${webhookUrl}" \
  -H "Content-Type: application/json" \
  -d '{
    "title": "Senior TypeScript Developer",
    "description": "Looking for a remote expert in Fastify and React.",
    "links": [{ "href": "https://example.com/job/123", "title": "Apply Here" }],
    "images": [{ "src": "https://example.com/preview.png" }]
  }'`
        },
        js: {
            label: "Node.js",
            code: `const response = await fetch("${webhookUrl}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    title: "Senior TypeScript Developer",
    description: "Looking for a remote expert in Fastify and React.",
    links: [{ href: "https://example.com/job/123", title: "Apply Here" }]
  }),
});

console.log(await response.text());`
        },
        python: {
            label: "Python",
            code: `import requests

url = "${webhookUrl}"
payload = {
    "title": "Senior TypeScript Developer",
    "description": "Looking for a remote expert in Fastify and React.",
    "links": [{ "href": "https://example.com/job/123" }]
}

response = requests.post(url, json=payload)
print(response.text)`
        },
        php: {
            label: "PHP",
            code: `<?php
$url = "${webhookUrl}";
$data = [
    "title" => "Senior TypeScript Developer",
    "description" => "Looking for a remote expert in Fastify and React."
];

$options = [
    "http" => [
        "header"  => "Content-type: application/json\r\n",
        "method"  => "POST",
        "content" => json_encode($data),
    ],
];

$context  = stream_context_create($options);
$result = file_get_contents($url, false, $context);
echo $result;
?>`
        }
    };

    return (
        <div className="max-w-[1000px] mx-auto grid gap-6 pb-12">
            
            <Card className="shadow-none">
                <CardHeader>
                    <CardTitle>Webhook URL</CardTitle>
                    <CardDescription>
                        Send a POST request to this endpoint to trigger the monitor.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex space-x-2">
                        <Input value={webhookUrl} readOnly className="bg-muted text-muted-foreground font-mono text-sm" />
                        <CopyToClipboardButton text={webhookUrl} />
                    </div>
                    <div className="text-sm text-muted-foreground">
                        <p>You can dynamically override the downstream webhook by adding the <code>ds_wh</code> query parameter:</p>
                        <code className="block mt-2 bg-slate-100 p-2 rounded text-slate-800 break-all">
                            ${webhookUrl}?ds_wh=https://your-custom-webhook.com/path
                        </code>
                    </div>
                </CardContent>
            </Card>

            <Card className="shadow-none overflow-hidden">
                <CardHeader>
                    <CardTitle>Usage Examples</CardTitle>
                    <CardDescription>
                        Integration snippets for common languages.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Tabs defaultValue="curl" className="w-full">
                        <TabsList className="grid w-full grid-cols-4 lg:w-[400px]">
                            {Object.entries(examples).map(([key, example]) => (
                                <TabsTrigger key={key} value={key}>{example.label}</TabsTrigger>
                            ))}
                        </TabsList>
                        {Object.entries(examples).map(([key, example]) => (
                            <TabsContent key={key} value={key} className="mt-4">
                                <div className="relative rounded-lg bg-slate-950 p-4 border border-slate-800 group overflow-hidden">
                                    <div className="absolute right-2 top-2">
                                        <CopyToClipboardButton 
                                            text={example.code} 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-slate-400 hover:text-slate-100 hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-all"
                                            iconClassName="size-4"
                                        />
                                    </div>
                                    <pre className="overflow-x-auto text-sm text-slate-50 font-mono leading-relaxed">
                                        <code>{example.code}</code>
                                    </pre>
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="shadow-none">
                    <CardHeader>
                        <CardTitle>Input Context</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground prose prose-slate max-w-none">
                        <p>When Hoofie processes your request, it builds a context for Lucene queries and AI analysis:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>JSON Input:</strong> Fields are mapped 1:1. You can query them like <code>category:"Job"</code>.</li>
                            <li><strong>Raw Text:</strong> The entire body is available in the <code>__body</code> field.</li>
                            <li><strong>Validation:</strong> If you provide <code>links</code> or <code>images</code> arrays, Hoofie will automatically validate and extract them into the <code>validated_data</code> object.</li>
                        </ul>
                    </CardContent>
                </Card>

                <Card className="shadow-none border-indigo-100 bg-indigo-50/20">
                    <CardHeader>
                        <CardTitle>Downstream Forwarding</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground prose prose-slate max-w-none">
                        <p>If a match is found, Hoofie forwards the request to your configured webhook:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>Enrichment:</strong> JSON payloads are injected with <code>user_data</code> (AI results) and <code>validated_data</code> (extracted links/images).</li>
                            <li><strong>Headers:</strong> All original headers are forwarded, except for <code>host</code>, <code>content-length</code>, and <code>connection</code>.</li>
                            <li><strong>Text payloads:</strong> Forwarded exactly as received to preserve the original format.</li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}